import React, { useState, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { formatDate as fd } from 'utils/date';
import { movementsUploadV2 } from 'service/movements-upload';

import { useDate, useGlobalApp, useScreen } from 'hooks';

import { filesResetted } from 'store/files';

import { SCREENS_KEYS } from 'constants/constants';
import {
  loadOperationLoadFileStatus,
  saveOperationLoadFileStatus,
} from 'core/storage/operationLoadFileStatusStorage';

import type LabelValue from 'commons/modules/label-value';

import FileHistoryContext from './context';
import type { FileHistoryContextType } from './types';

export const PAGE_DEFAULT = 1;
export const PAGE_SIZE_DEFAULT = 50;

interface Props {
  children: React.ReactNode;
}

export const FilesHistoryProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const {
    isValidDate,
    setIsValidDate,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
  } = useDate();
  const { onlyQuery } = useScreen(SCREENS_KEYS.operationLoad);
  const { investmentOfficeId } = useGlobalApp();

  const [currentLayout, setCurrentLayout] = useState<LabelValue | null>(null);
  const [currentFileType, setCurrentFileType] = useState<LabelValue | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [errorUploadFiles, setErrorUploadFiles] = useState(false);
  const [showCalendarHeatmap, setShowCalendarHeatmap] = useState(false);
  const [refreshData, setRefreshData] = useState(0);

  const [count, setCount] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [showDetailV2, setShowDetailV2] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  // Filtros para la tabla
  const [tableDate, setTableDate] = useState<Date | null>(null);
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [fileName, setFileName] = useState<string>('');
  const [folio, setFolio] = useState<string>('');
  const [brokerageHouse, setBrokerageHouse] = useState<LabelValue | null>(null);
  const [status, setStatus] = useState<string>(() =>
    loadOperationLoadFileStatus(),
  );

  const uploadFiles = async (files: File[]) =>
    movementsUploadV2({
      brokerageHouseId: currentLayout?.value || '',
      layoutTypeId: currentFileType?.value || '',
      files,
      date: selectedDate ? fd(selectedDate, 'yyyy-MM-dd') : '',
      investmentOfficeId,
    });

  const handleDateChange = (date: Date) => date && setSelectedDate(date);

  const handleReload = () => {
    dispatch(filesResetted());
    setRefreshData((value) => value + 1);
  };

  const resetPagination = () => {
    setPage(PAGE_DEFAULT);
    setPageSize(PAGE_SIZE_DEFAULT);
  };

  useEffect(() => {
    saveOperationLoadFileStatus(status);
  }, [status]);

  const value: FileHistoryContextType = {
    status,
    onlyQuery,
    uploadFiles,
    currentLayout,
    setCurrentLayout,
    currentFileType,
    setCurrentFileType,
    minDate,
    maxDate,
    selectedDate,
    setSelectedDate,
    handleDateChange,
    isValidDate,
    setIsValidDate,
    uploading,
    setUploading,
    errorUploadFiles,
    setErrorUploadFiles,
    showCalendarHeatmap,
    setShowCalendarHeatmap,
    refreshData,
    handleReload,
    tableDate,
    setTableDate,
    page,
    setPage,
    pageSize,
    setPageSize,
    count,
    setCount,
    PAGE_DEFAULT,
    PAGE_SIZE_DEFAULT,
    resetPagination,
    showDetail,
    showDetailV2,
    setShowDetail,
    setShowDetailV2,
    showConfirmDelete,
    setShowConfirmDelete,
    setStatus,
    fileName,
    setFileName,
    folio,
    setFolio,
    brokerageHouse,
    setBrokerageHouse,
  };

  return (
    <FileHistoryContext.Provider value={value}>
      {children}
    </FileHistoryContext.Provider>
  );
};

export default FilesHistoryProvider;
